/**
 * Duck containing all actions which go to or from the viewport.
 *
 * TODO: subdivide this duck if it gets out of hand
 *
 * Action Types:
 * - ASSET_ACTIVATED
 * - ASSET_UPLOAD_COMPLETED
 * - ASSET_UPLOAD_FILE_ADDED
 * - ASSET_UPLOAD_LOCAL_ASSET_READY
 * - CLEAR_VIEWPORT
 * - CURRENT_NAVIGATION_STEP_CHANGED
 * - FOCUS_ON_ASSET
 * - NAVIGATE_TO_NEXT_STEP
 * - NAVIGATE_TO_PREVIOUS_STEP
 * - NAVIGATION_STEPS_CHANGED
 * - UPLOAD_ASSET
 *
 * Action Creators:
 * - assetActivated
 * - assetUploadCompleted
 * - assetUploadFileAdded
 * - assetUploadLocalAssetReady
 * - clearViewport
 * - currentNavigationStepChanged
 * - focusOnAsset
 * - navigateToNextStep
 * - navigateToPreviousStep
 * - navigationStepsChanged
 * - uploadAsset
 */

import type {
  AssetActivated,
  AssetRecord,
  AssetUploadCompleted,
  AssetUploadFileAdded,
  AssetUploadLocalAssetReady,
  CardAsset,
  CardSide,
  ClearViewport,
  CurrentNavigationStepChanged,
  CurrentSideChanged,
  FocusOnAsset,
  NavigateToNextStep,
  NavigateToPreviousStep,
  NavigationStep,
  NavigationStepsChanged,
  RemoveSide,
  SaveAndNavigateToDelivery,
  SaveAndNavigateToDeliverySucceeded,
  SaveAndNavigateToDeliveryFailed,
  UploadAsset,
  ViewportAction,
  ViewportState,
} from '../types/viewport';

/** @type {ActionType} */
/* data action type constants */
export const CLEAR_VIEWPORT = 'viewport/data/CLEAR_VIEWPORT';

/* navigation action type constants */
export const CURRENT_NAVIGATION_STEP_CHANGED =
  'viewport/navigation/CURRENT_NAVIGATION_STEP_CHANGED';
export const CURRENT_SIDE_CHANGED = 'viewport/navigation/CURRENT_SIDE_CHANGED';

export const NAVIGATE_TO_NEXT_STEP =
  'viewport/navigation/NAVIGATE_TO_NEXT_STEP';
export const NAVIGATE_TO_PREVIOUS_STEP =
  'viewport/navigation/NAVIGATE_TO_PREVIOUS_STEP';
export const NAVIGATION_STEPS_CHANGED =
  'viewport/navigation/NAVIGATION_STEPS_CHANGED';

export const SAVE_AND_NAVIGATE_TO_DELIVERY =
  'viewport/event/SAVE_AND_NAVIGATE_TO_DELIVERY';
export const SAVE_AND_NAVIGATE_TO_DELIVERY_FAILED =
  'viewport/event/SAVE_AND_NAVIGATE_TO_DELIVERY_FAILED';
export const SAVE_AND_NAVIGATE_TO_DELIVERY_SUCCEEDED =
  'viewport/event/SAVE_AND_NAVIGATE_TO_DELIVERY_SUCCEEDED';

/* editing action type constants */
export const REMOVE_SIDE = 'viewport/assets/REMOVE_SIDE';

/* asset action type constants */
export const ASSET_ACTIVATED = 'viewport/assets/ASSET_ACTIVATED';
export const ASSET_UPLOAD_COMPLETED = 'viewport/assets/ASSET_UPLOAD_COMPLETED';
export const ASSET_UPLOAD_FILE_ADDED =
  'viewport/assets/ASSET_UPLOAD_FILE_ADDED';
export const ASSET_UPLOAD_LOCAL_ASSET_READY =
  'viewport/assets/ASSET_UPLOAD_LOCAL_ASSET_READY';
export const FOCUS_ON_ASSET = 'viewport/assets/FOCUS_ON_ASSET';
export const UPLOAD_ASSET = 'viewport/assets/UPLOAD_ASSET';

export const initialState = {
  currentNavigationStep: null,
  currentSide: {
    id: 0,
    side_type: '',
    orientation_type: '',
    num: 0,
    width: 0,
    height: 0,
    bounds: {},
    assets: [],
    image_url: '',
  },
  currentAsset: {
    abs_x: 0,
    abs_y: 0,
    asset_type: '',
    color: '',
    edited: false,
    filter: '',
    height: 0,
    image_url: '',
    positioning: {},
    rotation: 0,
    text: '',
    uuid: '',
    width: 0,
  },
  navigationSteps: [],
  saveRequested: false,
};

export default function reducer(
  state: ViewportState = initialState,
  action: ViewportAction,
): ViewportState {
  switch (action.type) {
    case ASSET_ACTIVATED:
      const { asset } = action.payload;
      return {
        ...state,
        currentAsset: asset,
      };
    case CLEAR_VIEWPORT:
      return { ...initialState };
    case CURRENT_NAVIGATION_STEP_CHANGED:
      const { step } = action.payload;
      return {
        ...state,
        currentNavigationStep: step,
      };
    case CURRENT_SIDE_CHANGED:
      const { side } = action.payload;
      return {
        ...state,
        currentSide: side,
      };
    case NAVIGATION_STEPS_CHANGED:
      const { steps } = action.payload;
      return {
        ...state,
        navigationSteps: steps,
      };
    case SAVE_AND_NAVIGATE_TO_DELIVERY:
      return { ...state, saveRequested: true };

    case SAVE_AND_NAVIGATE_TO_DELIVERY_FAILED:
    case SAVE_AND_NAVIGATE_TO_DELIVERY_SUCCEEDED:
      return { ...state, saveRequested: false };

    default:
      return state;
  }
}

/* ACTION CREATORS */
/* data */
export function clearViewport(): ClearViewport {
  return { type: CLEAR_VIEWPORT };
}

/* navigation */
export function currentNavigationStepChanged(
  step: NavigationStep,
): CurrentNavigationStepChanged {
  return {
    type: CURRENT_NAVIGATION_STEP_CHANGED,
    payload: { step },
  };
}

export function currentSideChanged(side: CardSide): CurrentSideChanged {
  return {
    type: CURRENT_SIDE_CHANGED,
    payload: { side },
  };
}

export function navigateToNextStep(): NavigateToNextStep {
  return { type: NAVIGATE_TO_NEXT_STEP };
}

export function navigateToPreviousStep(): NavigateToPreviousStep {
  return { type: NAVIGATE_TO_PREVIOUS_STEP };
}

export function navigationStepsChanged(
  steps: NavigationStep[],
): NavigationStepsChanged {
  return {
    type: NAVIGATION_STEPS_CHANGED,
    payload: { steps },
  };
}

export function saveAndNavigateToDelivery(): SaveAndNavigateToDelivery {
  return {
    type: SAVE_AND_NAVIGATE_TO_DELIVERY,
  };
}

export function saveAndNavigateToDeliveryFailed(): SaveAndNavigateToDeliveryFailed {
  return {
    type: SAVE_AND_NAVIGATE_TO_DELIVERY_FAILED,
  };
}

export function saveAndNavigateToDeliverySucceeded(): SaveAndNavigateToDeliverySucceeded {
  return {
    type: SAVE_AND_NAVIGATE_TO_DELIVERY_SUCCEEDED,
  };
}

/* assets */
/**
 * A notification that an asset has been "activated". In the viewport, this
 * means that a new asset has been focused, the selection range within a text
 * asset has changed, or an asset has been deselected with no replacement, in which case
 * the asset payload is null. In any case, the host may wish to update the UI
 * to reflect the new state of the viewport.
 */
export function assetActivated(asset: CardAsset): AssetActivated {
  return {
    type: ASSET_ACTIVATED,
    payload: { asset },
  };
}

export function assetUploadCompleted(options: {
  assetUUID: string,
  assetRecord: AssetRecord,
}): AssetUploadCompleted {
  return {
    type: ASSET_UPLOAD_COMPLETED,
    payload: options,
  };
}

export function assetUploadFileAdded(assetUUID: string): AssetUploadFileAdded {
  return {
    type: ASSET_UPLOAD_FILE_ADDED,
    payload: { assetUUID },
  };
}

/**
 * Before an asset is uploaded, a local version is generated for immediate use.
 * This action contains that local asset.
 *
 * @param {string} assetUUID - The UUID of the cardAsset to which the local asset will be applied.
 * @param {AssetRecord} assetRecord - The temporary local asset. Contains Base64 images.
 */
export function assetUploadLocalAssetReady(options: {
  assetUUID: string,
  assetRecord: AssetRecord,
}): AssetUploadLocalAssetReady {
  return {
    type: ASSET_UPLOAD_LOCAL_ASSET_READY,
    payload: options,
  };
}

/**
 * An action requesting that the viewport focus on the target asset.
 */
export function focusOnAsset(asset: CardAsset): FocusOnAsset {
  return {
    type: FOCUS_ON_ASSET,
    payload: { asset },
  };
}

/**
 * An action requesting that an upload of the supplied file.
 *
 * @param {string} options.assetType - The asset type to be uploaded.
 * @param {string} options.assetUUID - The UUID of the asset into which the uploaded
 * file should be placed, once transformed into a usable AssetRecord.
 * @param {File} options.file - The HTML5 File instance to be uploaded.
 */
export function uploadAsset(options: {
  assetType: string,
  assetUUID: string,
  file: File,
}): UploadAsset {
  return {
    type: UPLOAD_ASSET,
    payload: options,
  };
}

/* editing */
export function removeSide(sideType: string): RemoveSide {
  return {
    type: REMOVE_SIDE,
    payload: { sideType },
  };
}
